import type { FormFieldDef } from '@hcc/types';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';

/** Styling for the elements inside a rich_text block (preflight strips these). */
const RICH_TEXT_CLASS =
  'text-sm leading-relaxed text-fg ' +
  '[&_p]:mb-2 [&_p:last-child]:mb-0 ' +
  '[&_a]:text-accent [&_a]:underline [&_a:hover]:text-accent-hover ' +
  '[&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-0.5 ' +
  '[&_h1]:mb-1 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:mb-1 [&_h2]:text-base [&_h2]:font-semibold ' +
  '[&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold ' +
  '[&_strong]:font-semibold [&_b]:font-semibold [&_em]:italic ' +
  '[&_hr]:my-3 [&_hr]:border-line/10';

/**
 * The static, value-less field family: section_heading, paragraph,
 * rich_text and image. Pure display; no value, no validation.
 */
export function StaticFieldView({ field }: { field: FormFieldDef }) {
  if (field.type === 'section_heading') {
    return (
      <h3 className="border-b border-line/10 pb-1 text-base font-semibold text-fg">
        {field.label}
      </h3>
    );
  }
  if (field.type === 'paragraph') {
    return <p className="whitespace-pre-wrap text-sm text-fg-muted">{field.text ?? field.label}</p>;
  }
  if (field.type === 'rich_text') {
    return (
      <div
        className={RICH_TEXT_CLASS}
        // Sanitized at render so authored HTML can't inject active content.
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(field.text ?? '') }}
      />
    );
  }
  // image
  return field.image_key ? (
    <img
      src={field.image_key}
      alt={field.label}
      className="max-w-full rounded-token-sm border border-line/10"
    />
  ) : (
    <div className="rounded-token-sm border border-dashed border-line-strong/20 bg-surface-2 px-3 py-6 text-center text-xs text-fg-dim">
      {field.label || 'Image'} (no image set)
    </div>
  );
}
